import { getBlockContent, type StreamingBlock } from "@storytell/streaming";
import { createMemo, For, Show } from "solid-js";
import { safeParseBlockJson } from "../../message/parseBlockContent";
import { useChatRuntime } from "../../runtime/ChatRuntime";
import { formatIsoTimestamp } from "../../util/dateFormat";
import { LinearIcon } from "./linearIcon";
import { type LinearIssuesPayload, parseLinearIssuesPayload } from "./linearIssuesSchema";
import { LinearIssueMeta, safeLinearUrl } from "./linearRenderUtils";

interface LinearIssuesRendererProps {
  block: StreamingBlock;
}

export const LinearIssuesRenderer = (props: LinearIssuesRendererProps) => {
  const rt = useChatRuntime();

  const rawContent = createMemo(
    () =>
      (props.block.finalContent?.type === "text" ? props.block.finalContent.text : undefined) ??
      getBlockContent(props.block.blockId)
  );

  const payload = createMemo<LinearIssuesPayload | null>(() => {
    const raw = rawContent();
    if (!raw) return null;
    const json = safeParseBlockJson(raw);
    if (!json) return null;
    return parseLinearIssuesPayload(json);
  });

  const parseFailed = () => !!rawContent() && payload() === null;

  return (
    <Show
      when={!parseFailed()}
      fallback={
        <div class="rounded-lg border border-primary-border bg-panel px-3 py-2 text-xs text-muted-text">
          {rt.t("linear_issues_card.parse_error")}
        </div>
      }
    >
      <Show when={payload()}>
        {(data) => (
          // An agent fans these out several per turn, so a miss draws nothing.
          <Show when={data().issues.length > 0}>
            <div class="overflow-hidden rounded-lg border border-primary-border bg-panel">
              <div class="flex items-center gap-2 border-b border-primary-border px-3 py-2">
                <LinearIcon class="size-4 shrink-0 text-primary-text" />
                <span class="truncate text-sm font-medium text-primary-text">
                  <Show when={data().query} fallback={rt.t("linear_issues_card.title")}>
                    {(query) => rt.t("linear_issues_card.query_title", { query: query() })}
                  </Show>
                </span>
                <span class="ml-auto shrink-0 text-xs text-muted-text">
                  {data().issues.length === 1
                    ? rt.t("linear_issues_card.results_one")
                    : rt.t("linear_issues_card.results", { count: String(data().issues.length) })}
                </span>
              </div>

              <ul class="divide-y divide-primary-border">
                <For each={data().issues}>
                  {(issue) => (
                    <li class="px-3 py-2">
                      <div class="flex items-baseline justify-between gap-3">
                        <span class="flex min-w-0 items-baseline gap-1.5">
                          <span class="shrink-0 font-mono text-xs text-muted-text">{issue.identifier}</span>
                          <Show
                            when={safeLinearUrl(issue.url)}
                            fallback={<span class="truncate text-sm text-primary-text">{issue.title}</span>}
                          >
                            {(href) => (
                              <a
                                href={href()}
                                target="_blank"
                                rel="noopener noreferrer"
                                class="truncate text-sm text-brand hover:underline"
                              >
                                {issue.title}
                              </a>
                            )}
                          </Show>
                        </span>
                        <span class="shrink-0 text-xs text-muted-text">{formatIsoTimestamp(issue.updatedAt)}</span>
                      </div>
                      <LinearIssueMeta issue={issue} />
                    </li>
                  )}
                </For>
              </ul>

              <Show when={data().hasMore}>
                <div class="border-t border-primary-border px-3 py-2 text-xs text-muted-text">
                  {rt.t("linear_issues_card.has_more")}
                </div>
              </Show>
            </div>
          </Show>
        )}
      </Show>
    </Show>
  );
};
